<script lang="ts">
    import dayjs from 'dayjs';

    import { trans } from '$assets/shop-translator.js';
    import {
        defaultFormLabels,
        defaultFormValues,
    } from '$saga/views/Search/constants';
    import { filtersContext } from '$saga/views/Search/store';
    import {
        findCurrentSelectedFilterLabel,
        getFilterChangeHandler,
        isAlpineSkiingSelected,
        redirectToGenericSearch,
        shouldRedirectOnRemoval,
    } from '$saga/views/Search/utils';

    import FilterBadge from './FilterBadge.svelte';

    const debouncedHandleFilterChange = getFilterChangeHandler();

    /**
     * When a filter change value, auto submit form
     */
    const handleFormAutoSubmit = () => {
        debouncedHandleFilterChange();
    };
</script>

<div
    class="no-scrollbar flex flex-col gap-m overflow-x-auto rounded-m bg-container-quiet px-s py-xs"
>
    <span class="font-semibold">{trans('search.filter.sidebar.badges')}</span>
    <div
        class="flex max-h-[84px] flex-wrap gap-xs overflow-auto desktop:max-h-full"
    >
        {#if $filtersContext.formValues.destination}
            {#each $filtersContext.formValues.destination as dest (dest)}
                {#if String(dest) !== '' && dest != null}
                    {@const destLabel = findCurrentSelectedFilterLabel(
                        String(dest),
                        $filtersContext.defaultDestinationOptions ?? [],
                    )}
                    <FilterBadge
                        label={String(dest) === 'all'
                            ? trans('search.filter.sidebar.all_destinations')
                            : destLabel}
                        handleRemoveBadgeFilter={() => {
                            // Check if this is a pillar filter
                            if (
                                shouldRedirectOnRemoval(
                                    'destination',
                                    String(dest),
                                )
                            ) {
                                // Remove from formValues before redirect
                                $filtersContext.formValues.destination =
                                    $filtersContext.formValues.destination.filter(
                                        (d) => d !== String(dest),
                                    );
                                $filtersContext.formLabels.destination =
                                    $filtersContext.formLabels.destination.filter(
                                        (label) => label !== destLabel,
                                    );
                                // Redirect to generic search with remaining filters
                                redirectToGenericSearch(
                                    $filtersContext.formValues,
                                );
                            } else {
                                // Normal removal flow
                                $filtersContext.formValues.destination =
                                    $filtersContext.formValues.destination.filter(
                                        (d) => d !== String(dest),
                                    );
                                $filtersContext.formLabels.destination =
                                    $filtersContext.formLabels.destination.filter(
                                        (label) => label !== destLabel,
                                    );
                                handleFormAutoSubmit();
                            }
                        }}
                    />
                {/if}
            {/each}
        {/if}

        {#if $filtersContext.formValues.theme}
            {#each $filtersContext.formValues.theme as sport (sport)}
                {@const sportLabel = findCurrentSelectedFilterLabel(
                    String(sport),
                    $filtersContext.defaultSportOptions ?? [],
                )}
                {#if String(sport) !== '' && sport != null}
                    <FilterBadge
                        label={String(sport) === 'all'
                            ? trans('search.filter.sidebar.all_sports')
                            : sportLabel}
                        handleRemoveBadgeFilter={() => {
                            // Check if this is a pillar filter
                            if (
                                shouldRedirectOnRemoval('sport', String(sport))
                            ) {
                                // Remove from formValues before redirect
                                $filtersContext.formValues.theme =
                                    $filtersContext.formValues.theme.filter(
                                        (s) => s !== String(sport),
                                    );
                                $filtersContext.formLabels.theme =
                                    $filtersContext.formLabels.theme.filter(
                                        (label) => label !== sportLabel,
                                    );
                                // Redirect to generic search with remaining filters
                                redirectToGenericSearch(
                                    $filtersContext.formValues,
                                );
                            } else {
                                // Normal removal flow
                                $filtersContext.formValues.theme =
                                    $filtersContext.formValues.theme.filter(
                                        (s) => s !== String(sport),
                                    );
                                $filtersContext.formLabels.theme =
                                    $filtersContext.formLabels.theme.filter(
                                        (label) => label !== sportLabel,
                                    );
                                handleFormAutoSubmit();
                            }
                        }}
                    />
                {/if}
            {/each}
        {/if}

        {#if $filtersContext.formValues.nbPax && Number($filtersContext.formValues.nbPax) > 1}
            <FilterBadge
                label={`${$filtersContext.formValues.nbPax} ${trans('search.filter.sidebar.traveller')} ${Number($filtersContext.formValues.nbPax) > 1 ? 's' : ''}`}
                handleRemoveBadgeFilter={() => {
                    $filtersContext.formValues.nbPax = defaultFormValues.nbPax;
                    // $filtersContext.formValues.adults = defaultFormValues.adults;
                    // $filtersContext.formValues.children =
                    //     defaultFormValues.children;
                    $filtersContext.formLabels.nbPax = defaultFormLabels.nbPax;
                    handleFormAutoSubmit();
                }}
            />
        {/if}

        {#if $filtersContext.formValues.months && $filtersContext.formValues.months.length > 0}
            {#each $filtersContext.formValues.months as month (month)}
                <FilterBadge
                    label={dayjs(month, 'YYYY-MM').format('MMM YYYY')}
                    handleRemoveBadgeFilter={() => {
                        const monthIndex =
                            $filtersContext.formValues.months.indexOf(month);
                        $filtersContext.formValues.months =
                            $filtersContext.formValues.months.filter(
                                (m) => m !== month,
                            );
                        if (monthIndex >= 0) {
                            $filtersContext.formLabels.months =
                                $filtersContext.formLabels.months.filter(
                                    (_, idx) => idx !== monthIndex,
                                );
                        }
                        handleFormAutoSubmit();
                    }}
                />
            {/each}
        {:else if $filtersContext.formValues.startDate || $filtersContext.formValues.endDate}
            {@const start = $filtersContext.formValues.startDate
                ? dayjs($filtersContext.formValues.startDate.toString()).format(
                      'DD MMM YYYY',
                  )
                : null}
            {@const end = $filtersContext.formValues.endDate
                ? dayjs($filtersContext.formValues.endDate.toString()).format(
                      'DD MMM YYYY',
                  )
                : null}
            <FilterBadge
                label={`${start ?? ''} ${end ? ' - ' + end : ''}`}
                handleRemoveBadgeFilter={() => {
                    // Check if this is a pillar filter (for LastMinute page)
                    if (shouldRedirectOnRemoval('date')) {
                        // Remove from formValues before redirect
                        $filtersContext.formValues.startDate =
                            defaultFormValues.startDate;
                        $filtersContext.formValues.endDate =
                            defaultFormValues.endDate;
                        $filtersContext.formValues.flexibility =
                            defaultFormValues.flexibility;
                        $filtersContext.formLabels.startDate =
                            defaultFormLabels.startDate;
                        $filtersContext.formLabels.endDate =
                            defaultFormLabels.endDate;
                        // Redirect to generic search with remaining filters
                        redirectToGenericSearch($filtersContext.formValues);
                    } else {
                        // Normal removal flow
                        $filtersContext.formValues.startDate =
                            defaultFormValues.startDate;
                        $filtersContext.formValues.endDate =
                            defaultFormValues.endDate;
                        $filtersContext.formValues.flexibility =
                            defaultFormValues.flexibility;
                        $filtersContext.formLabels.startDate =
                            defaultFormLabels.startDate;
                        $filtersContext.formLabels.endDate =
                            defaultFormLabels.endDate;
                        handleFormAutoSubmit();
                    }
                }}
            />
        {/if}

        {#if $filtersContext.formValues.levels}
            {#each $filtersContext.formValues.levels as level (level)}
                <FilterBadge
                    label={findCurrentSelectedFilterLabel(
                        String(level),
                        $filtersContext.defaultLevels ?? [],
                    )}
                    handleRemoveBadgeFilter={() => {
                        $filtersContext.formValues.levels =
                            $filtersContext.formValues.levels.filter(
                                (l) => l !== level,
                            );
                        handleFormAutoSubmit();
                    }}
                />
            {/each}
        {/if}

        {#if $filtersContext.formValues.types}
            {#each $filtersContext.formValues.types as type (type)}
                <FilterBadge
                    label={findCurrentSelectedFilterLabel(
                        String(type),
                        $filtersContext.defaultTypes ?? [],
                    )}
                    handleRemoveBadgeFilter={() => {
                        $filtersContext.formValues.types =
                            $filtersContext.formValues.types.filter(
                                (t) => t !== type,
                            );
                        handleFormAutoSubmit();
                    }}
                />
            {/each}
        {/if}

        {#if isAlpineSkiingSelected($filtersContext.formValues.theme, $filtersContext.alpineSkiingSportId) && $filtersContext.formValues.resorts.length > 0}
            {#each $filtersContext.formValues.resorts as resort (resort)}
                {@const resortLabel = findCurrentSelectedFilterLabel(
                    String(resort),
                    $filtersContext.defaultSkiResorts ?? [],
                )}
                {#if resortLabel !== ''}
                    <FilterBadge
                        label={resortLabel}
                        handleRemoveBadgeFilter={() => {
                            $filtersContext.formValues.resorts =
                                $filtersContext.formValues.resorts.filter(
                                    (selectedResort) =>
                                        selectedResort !== resort,
                                );
                            handleFormAutoSubmit();
                        }}
                    />
                {/if}
            {/each}
        {/if}

        {#if $filtersContext.formValues.tags}
            {#each $filtersContext.formValues.tags as tag (tag)}
                {@const tagLabel = findCurrentSelectedFilterLabel(
                    String(tag),
                    $filtersContext.defaultTags ?? [],
                )}
                {#if tagLabel !== ''}
                    <FilterBadge
                        label={tagLabel}
                        handleRemoveBadgeFilter={() => {
                            // Check if this is a pillar filter
                            if (shouldRedirectOnRemoval('tag', String(tag))) {
                                // Remove from formValues before redirect
                                $filtersContext.formValues.tags =
                                    $filtersContext.formValues.tags.filter(
                                        (t) => t !== tag,
                                    );
                                // Redirect to generic search with remaining filters
                                redirectToGenericSearch(
                                    $filtersContext.formValues,
                                );
                            } else {
                                // Normal removal flow
                                $filtersContext.formValues.tags =
                                    $filtersContext.formValues.tags.filter(
                                        (t) => t !== tag,
                                    );
                                handleFormAutoSubmit();
                            }
                        }}
                    />
                {/if}
            {/each}
        {/if}

        {#if null !== $filtersContext.formValues.budgetMin && null !== $filtersContext.formValues.budgetMax && ($filtersContext.formValues.budgetMin !== defaultFormValues.budgetMin || $filtersContext.formValues.budgetMax !== defaultFormValues.budgetMax)}
            <FilterBadge
                label={`${trans('search.filter.sidebar.between')} ${$filtersContext.formValues.budgetMin} ${trans('search.filter.sidebar.and')} ${$filtersContext.formValues.budgetMax} €`}
                handleRemoveBadgeFilter={() => {
                    $filtersContext.formValues.budgetMax =
                        defaultFormValues.budgetMax;
                    $filtersContext.formValues.budgetMin =
                        defaultFormValues.budgetMin;
                    handleFormAutoSubmit();
                }}
            />
        {/if}

        {#if $filtersContext.formValues.durationMin && $filtersContext.formValues.durationMax && ($filtersContext.formValues.durationMin !== defaultFormValues.durationMin || $filtersContext.formValues.durationMax !== defaultFormValues.durationMax)}
            <FilterBadge
                label={`${trans('search.filter.sidebar.between')} ${$filtersContext.formValues.durationMin} ${trans('search.filter.sidebar.and')} ${$filtersContext.formValues.durationMax} ${trans('search.filter.sidebar.days')}`}
                handleRemoveBadgeFilter={() => {
                    $filtersContext.formValues.durationMin =
                        defaultFormValues.durationMin;
                    $filtersContext.formValues.durationMax =
                        defaultFormValues.durationMax;
                    handleFormAutoSubmit();
                }}
            />
        {/if}
    </div>
</div>
